import { TZDateMini } from '@date-fns/tz';
import {
  format,
  isBefore,
  isSameDay,
  isSameMinute,
  isSameYear,
  isToday,
} from 'date-fns';
import { type IntlShape, type MessageDescriptor } from 'react-intl';

import { formatDate, type FormatDateOptions } from '@dynatrace-sdk/units';
import {
  getRegionalFormat,
  getTimezone,
} from '@dynatrace-sdk/user-preferences';

import type { TimeDetails } from '../../core/types/time.js';
import {
  TIMEFRAME_EXPRESSION,
  parseTime,
} from '../../core/utils/timeParser.js';
import { messages } from '../../forms/forms-core/time/timeParser.js';

const DATE_YEAR_SEPARATOR = ', ';

/**
 * Returns expression in e.g. 'Last 5 minutes' format.
 */
function formatRelativeToNow(
  expression: string,
  prefix: 'future' | 'past' | 'relativeFuture' | 'other',
  intl: IntlShape,
): string {
  const [, offsetString, unit] = expression.match(TIMEFRAME_EXPRESSION) ?? [];
  const offset = Math.abs(parseInt(offsetString));

  if (offset === 0) {
    return intl.formatMessage(messages.now);
  }

  const timeExpression = messages[unit as keyof typeof messages];
  if (timeExpression) {
    prefix ??= offsetString.includes('+') ? 'future' : 'past';

    return intl
      .formatMessage(timeExpression, {
        prefix,
        offset,
        suffix: '',
      })
      .trim();
  }

  return intl.formatMessage(messages.invalid);
}

/**
 * Returns expression or formatted date time if format is iso8601.
 */
function formatDetails(details: TimeDetails): string {
  return details.type === 'expression'
    ? details.normalized
    : format(details.date, 'yyyy-MM-dd HH:mm');
}

/**
 * Return formatted relative expression for given time details.
 */
function formatRelativeExpressions(
  from: TimeDetails,
  to: TimeDetails,
  intl: IntlShape,
): string | undefined {
  if (to.normalized === 'now' && from.normalized === 'now') {
    return intl.formatMessage(messages.invalid);
  }

  if (to.normalized === 'now') {
    return formatRelativeToNow(from.normalized, 'past', intl);
  } else if (from.normalized === 'now') {
    return formatRelativeToNow(to.normalized, 'future', intl);
  }
}

/**
 * Return proper display string for given timeframe.
 * @deprecated Will be removed, as the TimeFrame type changed to TimeFrameV2
 * @public
 */
export function formatTimeframe(
  { from, to = 'now' }: { from?: string; to?: string },
  intl: IntlShape,
): string {
  /* eslint-disable @typescript-eslint/no-deprecated */
  const fromDetails = parseTime(from);
  const toDetails = parseTime(to);
  /* eslint-enable @typescript-eslint/no-deprecated */

  if (!fromDetails || !toDetails) {
    return intl.formatMessage(messages.invalid);
  }

  let formatted: string | undefined;
  if (fromDetails.type === 'expression' && toDetails.type === 'expression') {
    formatted = formatRelativeExpressions(fromDetails, toDetails, intl);
  }

  return (
    formatted ??
    `${formatDetails(fromDetails)} ${intl.formatMessage(
      messages.to,
    )} ${formatDetails(toDetails)}`
  );
}

/**
 * Utility function that facilitates the formatting of a timeframe
 * The function creates the from-string and to-string respectively and returns
 * the timeframe parts as an array of strings. The IntlShape passed to the
 * function is used to determine the language, while the IntlShape created
 * in the function (intlTimeFormat) is used to determine the type of time
 * display.
 *
 * @param from - Start date in the timeframe
 * @param to - End date in the timeframe
 * @param intl - Intl object
 * @param clampFutureToDateToNow - If false, the to date will not be clamped to `Now`
 * @returns an array of timeframe parts.
 * @public
 */
export function formatTimeframeToParts(
  from: Date,
  to: Date,
  intl: IntlShape,
  clampFutureToDateToNow: boolean = true,
  separator: MessageDescriptor = messages.to,
): string | [string, string, string] {
  const locale = getRegionalFormat();

  const now = TZDateMini.tz(getTimezone());
  const isNowInTimeframe = isBefore(from, now) && !isBefore(to, now);
  const _to = isNowInTimeframe && clampFutureToDateToNow ? now : to;

  const yearFormatOptions: FormatDateOptions = {
    year: 'numeric',
    locale,
  };
  const dateFormatOptions: FormatDateOptions = {
    month: 'short',
    day: '2-digit',
    locale,
  };
  const timeFormatOptions: FormatDateOptions = {
    hour: '2-digit',
    minute: '2-digit',
    locale,
  };

  const formatOptions = {
    yearFormatOptions,
    dateFormatOptions,
    timeFormatOptions,
  };

  const fromStr = getFromString(from, _to, intl, formatOptions);
  const toStr = getToString(from, _to, intl, formatOptions);

  const textSeparator = intl.formatMessage(separator);

  return [fromStr, textSeparator, toStr];
}

const getFromString = (
  from: Date,
  to: Date,
  intl: IntlShape,
  formatOptions: {
    dateFormatOptions: FormatDateOptions;
    yearFormatOptions: FormatDateOptions;
    timeFormatOptions: FormatDateOptions;
  },
) => {
  const currentDate = TZDateMini.tz(getTimezone());

  const date = isToday(from)
    ? intl.formatMessage(messages.today)
    : formatDate(from, formatOptions.dateFormatOptions);

  const year =
    isSameYear(from, currentDate) ||
    (isSameYear(from, to) && !isSameDay(from, to))
      ? ''
      : formatDate(from, formatOptions.yearFormatOptions) + DATE_YEAR_SEPARATOR;

  const time = formatDate(from, formatOptions.timeFormatOptions);

  return `${date}${DATE_YEAR_SEPARATOR}${year}${time}`;
};

const getToString = (
  from: Date,
  to: Date,
  intl: IntlShape,
  formatOptions: {
    dateFormatOptions: FormatDateOptions;
    yearFormatOptions: FormatDateOptions;
    timeFormatOptions: FormatDateOptions;
  },
) => {
  const currentDate = TZDateMini.tz(getTimezone());

  let date =
    formatDate(to, formatOptions.dateFormatOptions) + DATE_YEAR_SEPARATOR;
  if (isSameDay(from, to) || isSameMinute(to, currentDate)) {
    date = '';
  } else if (isToday(to)) {
    date = intl.formatMessage(messages.today) + DATE_YEAR_SEPARATOR;
  }

  const year =
    (isSameYear(to, currentDate) && isSameYear(from, to)) ||
    isSameDay(to, from) ||
    isSameDay(to, currentDate)
      ? ''
      : formatDate(to, formatOptions.yearFormatOptions) + DATE_YEAR_SEPARATOR;

  const time = isSameMinute(to, currentDate)
    ? intl.formatMessage(messages.now)
    : formatDate(to, formatOptions.timeFormatOptions);

  return `${date}${year}${time}`;
};

/**
 * Formats a plain date-based timeframe into a string, for display.
 * @param from - Start date in the timeframe
 * @param to - End date in the timeframe
 * @param intl - Intl object
 * @returns the formatted string
 * @internal
 */
export function formatTimeframePlain(
  from: Date,
  to: Date,
  intl: IntlShape,
): string {
  const timeframeParts = formatTimeframeToParts(from, to, intl);
  return typeof timeframeParts === 'string'
    ? timeframeParts
    : timeframeParts.join(' ');
}
